import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useRazorpayPayment } from '../hooks/useRazorpay';
import { usePricingConfig } from '../hooks/usePricingConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      return await res.json();
    } catch (err) { return {}; }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) { return { error: err.message }; }
  }
};

function WalletPage({ token, user }) {
  const { language } = useLanguage();
  const { rechargeWallet, loading: paymentLoading, error: paymentError } = useRazorpayPayment();
  const { config: pricingCfg } = usePricingConfig();
  const MIN_RECHARGE = pricingCfg.min_wallet_recharge;
  const CURR = pricingCfg.currency_symbol;
  const [walletData, setWalletData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [recharging, setRecharging] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const loadWalletData = async () => {
    try {
      const schoolId = user?.school_id || 'default_school';
      const [balanceRes, summaryRes, packagesRes, transRes] = await Promise.all([
        api.get(`/api/wallet/balance/${schoolId}`, token),
        api.get(`/api/wallet/summary/${schoolId}`, token),
        api.get('/api/wallet/packages', token),
        api.get(`/api/wallet/transactions/recent/${schoolId}`, token)
      ]);
      setWalletData(balanceRes);
      setSummary(summaryRes?.summary);
      setPackages(packagesRes?.packages || []);
      setTransactions(transRes?.transactions || []);
    } catch (err) {
      console.error('Error loading wallet data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWalletData();
  }, []);

  const handleRecharge = async (amount) => {
    try {
      setRecharging(true);
      const schoolId = user?.school_id || 'default_school';
      
      // Use Razorpay for payment
      await rechargeWallet({
        schoolId,
        amount: parseFloat(amount),
        userName: user?.name || '',
        userPhone: user?.phone || '',
        userEmail: user?.email || '',
        onSuccess: async (paymentData) => {
          // After successful Razorpay payment, credit to wallet
          try {
            await api.post(`/api/wallet/recharge/${schoolId}`, {
              amount: parseFloat(amount),
              payment_method: 'razorpay',
              payment_id: paymentData.paymentId,
              order_id: paymentData.orderId,
              notes: selectedPackage ? `Package: ${selectedPackage.name}` : 'Custom recharge'
            }, token);
            
            setPaymentSuccess(true);
            setShowRechargeModal(false);
            setSelectedPackage(null);
            setCustomAmount('');
            loadWalletData();
            
            // Show success message for 3 seconds
            setTimeout(() => setPaymentSuccess(false), 3000);
          } catch (err) {
            console.error('Wallet credit error:', err);
            alert(language === 'hi' ? 'भुगतान सफल, लेकिन वॉलेट अपडेट में त्रुटि। कृपया सपोर्ट से संपर्क करें।' : 'Payment successful but wallet update failed. Please contact support.');
          }
        },
        onFailure: (error) => {
          console.error('Payment failed:', error);
          if (error.message !== 'Payment cancelled') {
            alert(language === 'hi' ? `भुगतान विफल: ${error.message || error.description || 'कुछ गलत हो गया'}` : `Payment failed: ${error.message || error.description || 'Something went wrong'}`);
          }
        }
      });
    } catch (err) {
      console.error('Recharge error:', err);
      alert(err.message || 'Recharge failed');
    } finally {
      setRecharging(false);
    }
  };

  const downloadReport = async (period) => {
    try {
      setDownloadingReport(true);
      const schoolId = user?.school_id || 'default_school';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/wallet/report/${schoolId}?period=${period}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AI_Usage_Report_${period}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const balance = walletData?.wallet?.balance || 0;

  return (
    <div className="space-y-6" data-testid="wallet-page">
      {/* Payment Success Alert */}
      {paymentSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-bounce">
          <span className="text-2xl">✅</span>
          <div>
            <p className="font-bold">{language === 'hi' ? 'भुगतान सफल!' : 'Payment Successful!'}</p>
            <p className="text-sm text-green-100">{language === 'hi' ? 'वॉलेट में राशि जोड़ दी गई है' : 'Amount added to wallet'}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {language === 'hi' ? '💳 स्कूल Wallet' : '💳 School Wallet'}
          </h2>
          <p className="text-gray-500 text-sm">
            {language === 'hi' ? 'AI Credits प्रबंधन और रिचार्ज' : 'Manage AI Credits & Recharge'}
          </p>
        </div>
        <button
          onClick={() => setShowRechargeModal(true)}
          className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 font-semibold shadow-lg transform hover:scale-105 transition-all"
          data-testid="recharge-btn"
        >
          {language === 'hi' ? '➕ रिचार्ज करें' : '➕ Add Money'}
        </button>
      </div>

      {/* Alerts */}
      {walletData?.alerts?.length > 0 && (
        <div className="space-y-2">
          {walletData.alerts.map((alert, idx) => (
            <div key={idx} className={`p-4 rounded-xl ${
              alert.severity === 'critical' ? 'bg-red-100 border-2 border-red-400 text-red-800' : 
              'bg-yellow-100 border-2 border-yellow-400 text-yellow-800'
            }`}>
              <p className="font-medium">{language === 'hi' ? alert.message : alert.message_en}</p>
            </div>
          ))}
        </div>
      )}

      {/* Balance Card */}
      <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-red-500 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-orange-100 text-sm">{language === 'hi' ? 'वर्तमान बैलेंस' : 'Current Balance'}</p>
            <p className="text-5xl font-bold mt-2">₹{balance.toFixed(2)}</p>
            <p className="text-orange-200 text-sm mt-2">
              {language === 'hi' ? `इस महीने खर्च: ₹${walletData?.wallet?.this_month_spent || 0}` : 
               `This month spent: ₹${walletData?.wallet?.this_month_spent || 0}`}
            </p>
          </div>
          <div className="text-right">
            <div className="bg-white/20 rounded-xl p-4">
              <p className="text-orange-100 text-xs">{language === 'hi' ? 'अनुमानित दिन शेष' : 'Est. Days Left'}</p>
              <p className="text-3xl font-bold">{summary?.projected_days_remaining || '∞'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-5 text-center">
          <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल रिचार्ज' : 'Total Recharged'}</p>
          <p className="text-2xl font-bold text-green-600 mt-1">₹{summary?.total_recharged || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5 text-center">
          <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल खर्च' : 'Total Spent'}</p>
          <p className="text-2xl font-bold text-red-500 mt-1">₹{summary?.total_spent?.toFixed(2) || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5 text-center">
          <p className="text-gray-500 text-sm">{language === 'hi' ? 'इस महीने' : 'This Month'}</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">₹{summary?.this_month?.spent?.toFixed(2) || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5 text-center">
          <p className="text-gray-500 text-sm">{language === 'hi' ? 'दैनिक औसत' : 'Daily Avg'}</p>
          <p className="text-2xl font-bold text-purple-600 mt-1">₹{summary?.daily_average?.toFixed(2) || 0}</p>
        </div>
      </div>

      {/* Download Reports */}
      <div className="bg-white rounded-xl shadow p-5">
        <h3 className="font-semibold text-gray-800 mb-4">
          {language === 'hi' ? '📄 AI Usage रिपोर्ट डाउनलोड करें' : '📄 Download AI Usage Reports'}
        </h3>
        <div className="flex gap-4 flex-wrap">
          <button
            onClick={() => downloadReport('weekly')}
            disabled={downloadingReport}
            className="px-5 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
            data-testid="download-weekly-btn"
          >
            📊 {language === 'hi' ? 'साप्ताहिक रिपोर्ट (PDF)' : 'Weekly Report (PDF)'}
          </button>
          <button
            onClick={() => downloadReport('monthly')}
            disabled={downloadingReport}
            className="px-5 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2"
            data-testid="download-monthly-btn"
          >
            📈 {language === 'hi' ? 'मासिक रिपोर्ट (PDF)' : 'Monthly Report (PDF)'}
          </button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="p-4 bg-gray-50 border-b">
          <h3 className="font-semibold text-gray-800">
            {language === 'hi' ? '📋 हाल के लेनदेन' : '📋 Recent Transactions'}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="transactions-table">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'तारीख' : 'Date'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'प्रकार' : 'Type'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'विवरण' : 'Description'}</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">{language === 'hi' ? 'राशि' : 'Amount'}</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">{language === 'hi' ? 'बैलेंस' : 'Balance'}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    {language === 'hi' ? 'कोई लेनदेन नहीं' : 'No transactions yet'}
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      {new Date(tx.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tx.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {tx.type === 'credit' ? '➕ Credit' : '➖ Debit'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {tx.category === 'recharge' ? (language === 'hi' ? 'वॉलेट रिचार्ज' : 'Wallet Recharge') :
                       tx.category === 'ai_usage' ? (language === 'hi' ? `AI उपयोग: ${tx.feature_name || ''}` : `AI Usage: ${tx.feature_name || ''}`) :
                       tx.notes || tx.category}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${
                      tx.type === 'credit' ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {tx.type === 'credit' ? '+' : '-'}₹{tx.amount?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">
                      ₹{tx.balance_after?.toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recharge Modal */}
      {showRechargeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recharge-modal">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">
                  {language === 'hi' ? '💳 Wallet रिचार्ज करें' : '💳 Recharge Wallet'}
                </h3>
                <button 
                  onClick={() => {setShowRechargeModal(false); setSelectedPackage(null);}}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Packages */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3">
                  {language === 'hi' ? '🎁 रिचार्ज पैकेज चुनें' : '🎁 Select Recharge Package'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => {setSelectedPackage(pkg); setCustomAmount('');}}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedPackage?.id === pkg.id 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-gray-200 hover:border-orange-300'
                      } ${pkg.popular ? 'ring-2 ring-orange-400' : ''}`}
                    >
                      {pkg.popular && (
                        <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                          {language === 'hi' ? '⭐ लोकप्रिय' : '⭐ Popular'}
                        </span>
                      )}
                      <h5 className="font-bold text-lg mt-2">{language === 'hi' ? pkg.name_hi : pkg.name}</h5>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-2xl font-bold text-gray-800">₹{pkg.amount}</span>
                        {pkg.bonus > 0 && (
                          <span className="text-green-600 text-sm">+₹{pkg.bonus} bonus</span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm mt-1">
                        {language === 'hi' ? pkg.description_hi : pkg.description}
                      </p>
                      <p className="text-orange-600 font-semibold mt-2">
                        {language === 'hi' ? 'कुल क्रेडिट्स' : 'Total Credits'}: ₹{pkg.total_credits}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-700 mb-3">
                  {language === 'hi' ? '✏️ या कस्टम राशि डालें' : '✏️ Or Enter Custom Amount'}
                </h4>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => {setCustomAmount(e.target.value); setSelectedPackage(null);}}
                      placeholder={language === 'hi' ? `राशि डालें (min ${CURR}${MIN_RECHARGE})` : `Enter amount (min ${CURR}${MIN_RECHARGE})`}
                      className="w-full pl-8 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500"
                      min={MIN_RECHARGE}
                      data-testid="custom-amount-input"
                    />
                  </div>
                </div>
              </div>

              {/* Confirm Button */}
              <div className="border-t pt-4">
                <button
                  onClick={() => {
                    const amount = selectedPackage ? selectedPackage.total_credits : parseFloat(customAmount);
                    if (amount >= MIN_RECHARGE) {
                      handleRecharge(amount);
                    } else {
                      alert(language === 'hi' ? `न्यूनतम राशि ${CURR}${MIN_RECHARGE} है` : `Minimum amount is ${CURR}${MIN_RECHARGE}`);
                    }
                  }}
                  disabled={recharging || paymentLoading || (!selectedPackage && (!customAmount || parseFloat(customAmount) < MIN_RECHARGE))}
                  className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-recharge-btn"
                >
                  {(recharging || paymentLoading) ? (
                    <>
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                      {language === 'hi' ? 'प्रोसेसिंग...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {language === 'hi' ? 'Pay करें' : 'Pay Now'} 
                      {selectedPackage ? ` ₹${selectedPackage.amount}` : customAmount ? ` ₹${customAmount}` : ''}
                    </>
                  )}
                </button>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <img src="https://razorpay.com/favicon.png" alt="Razorpay" className="h-4 w-4" />
                  <p className="text-xs text-gray-500">
                    {language === 'hi' ? 'Razorpay द्वारा सुरक्षित भुगतान • UPI, Card, NetBanking' : 'Secured by Razorpay • UPI, Card, NetBanking'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Today AI Usage Page

export default WalletPage;
